import AppStoreCatalogStatus from '@/components/Credits/AppStoreCatalogStatus';
import { usesHostedBilling, usesStoreBilling } from '@/lib/appDistribution';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useAuthStore, useCreditPacksStore, useModalStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { useBillingPalette } from '@/components/Settings/billing/BillingGroup';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import { nativeCreditPackPrice } from '@/lib/nativePayment';
import { trackEvent } from '@/constants/utils/analytics';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PayWithBitcoinRow from './PayWithBitcoinRow';
import InternationalPaymentNotice from '@/components/InternationalPaymentNotice';
import BannerPlacement from '@/components/Banner/BannerPlacement';

export default function CreditStorePacks({ promoCode }) {
  const palette = useBillingPalette();
  const styles = useMemo(() => createStyles(palette), [palette]);
  const { openCreditPacksCheckout, usesWhopCheckout, iap } = useGabPurchase();
  const { openModal } = useModalStore(state => state.actions);
  const user = useAuthStore(state => state.user);
  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const fetchCreditPacks = useCreditPacksStore(state => state.fetchCreditPacks);
  const isFetching = useCreditPacksStore(state => state.isLoading);
  const fetchError = useCreditPacksStore(state => state.error);
  const clearCreditPacks = useCreditPacksStore(state => state.clearCreditPacks);
  const [selectedId, setSelectedId] = useState(null);
  const [isBuying, setIsBuying] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const buyingRef = useRef(false);
  const packRefs = useRef([]);
  const directBilling = usesHostedBilling();

  useEffect(() => { Promise.resolve(fetchCreditPacks()).catch(() => {}); }, [fetchCreditPacks]);

  const packs = useMemo(() => [...(creditPacks || [])]
    .filter(pack => pack.flags?.isDisplayed !== false && !pack.deletedAt && (!usesStoreBilling() || getCreditPackIapProductId(pack.credits)))
    .sort((a, b) => a.price - b.price), [creditPacks]);
  // Use a valid selection even if the catalog changes while the store is open.
  const selectedPack = packs.find(pack => pack.id === selectedId)
    || packs.find(pack => pack.flags?.isBestValue) || packs[0];

  const priceLabel = useCallback(pack => {
    if (!directBilling) {
      const productId = getCreditPackIapProductId(pack.credits);
      const product = productId ? iap?.getProduct?.(productId) : null;
      return nativeCreditPackPrice(product) || 'Store price unavailable';
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: pack.currency || 'USD' }).format(pack.price ?? 0);
  }, [directBilling, iap]);

  const purchaseBlocked = isBuying || (usesStoreBilling() && (iap?.purchasePending || iap?.purchaseInProgress
    || !nativeCreditPackPrice(iap?.getProduct?.(getCreditPackIapProductId(selectedPack?.credits)))));

  const handleBuy = async () => {
    if (!selectedPack || purchaseBlocked || buyingRef.current || isFetching || fetchError) return;
    buyingRef.current = true;
    setIsBuying(true);
    setPurchaseError('');
    trackEvent('conversion', 'credit_pack_selected', String(selectedPack.credits), selectedPack.price);
    try {
      const canQuickPurchase = directBilling && !usesWhopCheckout && !promoCode
        && user?.valmar?.hasPaymentMethod && user?.valmar?.maskedCardData?.maskedNumber;
      if (canQuickPurchase) {
        openModal('QuickPurchaseConfirmModal', { creditPackId: selectedPack.id });
      } else {
        await openCreditPacksCheckout({ creditPackId: selectedPack.id, credits: selectedPack.credits, promoCode });
      }
    } catch (error) {
      setPurchaseError(error?.message || 'Checkout could not open. Please try again.');
    } finally {
      buyingRef.current = false;
      setIsBuying(false);
    }
  };

  const selectPack = id => { setSelectedId(id); setPurchaseError(''); };
  const onPackKeyDown = (event, index) => {
    const key = event.nativeEvent?.key || event.key;
    const offsets = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(key in offsets) && key !== 'Home' && key !== 'End') return;
    event.preventDefault();
    const next = key === 'Home' ? 0 : key === 'End' ? packs.length - 1 : (index + offsets[key] + packs.length) % packs.length;
    selectPack(packs[next].id);
    packRefs.current[next]?.focus();
  };

  return (
    <View style={styles.container}>
      <View style={styles.heading}>
        <Text tag="h2" size="l" weight="bold">Choose a credit pack</Text>
        <Text size="s" color="secondary">One-time purchase. Yours to use anytime.</Text>
      </View>
      <AppStoreCatalogStatus iap={iap} credits />
      <BannerPlacement placement="credit-store" />
      <InternationalPaymentNotice />
      {isFetching ? <View style={styles.status}><ActivityIndicator color={palette.ink} /><Text size="s" color="secondary">Loading credit packs…</Text></View>
        : fetchError ? <View style={styles.status}>
          <Text size="s" color="danger" accessibilityRole="alert">Credit packs couldn’t load. Please try again.</Text>
          <Pressable accessibilityRole="button" style={styles.retry} onPress={() => { clearCreditPacks(); Promise.resolve(fetchCreditPacks()).catch(() => {}); }}><Text size="s" weight="bold">Try again</Text></Pressable>
        </View>
        : !packs.length ? <Text size="s" color="secondary">No credit packs are available right now. Please check back soon.</Text>
        : <>
          <View accessibilityRole="radiogroup" accessibilityLabel="Credit packs" style={styles.packList}>
            {packs.map((pack, index) => {
              const selected = selectedPack?.id === pack.id;
              const badge = pack.flags?.isBestValue ? 'Best value' : pack.flags?.isPopular ? 'Popular' : null;
              return <Pressable key={pack.id} ref={node => { packRefs.current[index] = node; }}
                accessibilityRole="radio" accessibilityLabel={`${pack.credits.toLocaleString()} credits, ${priceLabel(pack)}${badge ? `, ${badge}` : ''}`}
                accessibilityState={{ checked: selected, disabled: isBuying }} disabled={isBuying}
                {...(Platform.OS === 'web' ? { 'aria-checked': selected, tabIndex: selected ? 0 : -1, onKeyDown: event => onPackKeyDown(event, index) } : {})}
                onPress={() => selectPack(pack.id)}
                style={({ hovered, pressed }) => [styles.pack, (hovered || pressed) && styles.hover, selected && styles.selected, isBuying && { opacity: 0.6 }]}>
                <View style={[styles.radio, selected && styles.radioSelected]}>{selected && <View style={styles.radioDot} />}</View>
                <View style={styles.packCopy}>
                  <View style={styles.packTitle}><Text size="m" weight="bold">{pack.credits.toLocaleString()}</Text><Text size="s" color="secondary">credits</Text></View>
                  {!!badge && <Text size="xxs" weight="medium" color="secondary">{badge}</Text>}
                </View>
                <Text size="m" weight="bold">{priceLabel(pack)}</Text>
              </Pressable>;
            })}
          </View>
          <View style={styles.checkout}>
            <Pressable accessibilityRole="button" accessibilityState={{ disabled: purchaseBlocked, busy: isBuying }}
              {...(Platform.OS === 'web' ? { 'aria-busy': isBuying } : {})}
              disabled={purchaseBlocked} onPress={handleBuy}
              style={({ hovered, pressed }) => [styles.buy, { opacity: isBuying ? 0.65 : pressed ? 0.75 : hovered ? 0.9 : 1 }]}>
              {isBuying ? <ActivityIndicator size="small" color={palette.inkInverse} /> : <Icon name={directBilling ? 'credit-card' : 'plus'} size={14} color={palette.inkInverse} />}
              <Text size="n" weight="bold" style={styles.buyLabel}>{isBuying ? 'Opening checkout…' : `Continue · ${priceLabel(selectedPack)}`}</Text>
              {!isBuying && <Icon name="arrow-right" size={12} color={palette.inkInverse} />}
            </Pressable>
            <Text size="xs" color="secondary" style={styles.center}>{selectedPack.credits.toLocaleString()} credits · No subscription</Text>
            {!!purchaseError && <Text size="s" color="danger" accessibilityRole="alert">{purchaseError}</Text>}
            <PayWithBitcoinRow creditPackId={selectedPack.id} price={selectedPack.price} disabled={isBuying} style={styles.bitcoin} />
          </View>
        </>}
      <View style={styles.trust}><Icon name="lock" size={11} color={palette.colors.text_color_secondary} /><Text size="xs" color="secondary">Secure checkout · Credits never expire</Text></View>
    </View>
  );
}

const createStyles = palette => StyleSheet.create({
  container: { gap: 16, width: '100%' },
  heading: { gap: 5 },
  packList: { gap: 8 },
  pack: { flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 60, paddingVertical: 10, paddingHorizontal: 16, borderRadius: 13, borderWidth: 1, borderColor: palette.separator },
  hover: { backgroundColor: palette.hover },
  selected: { borderColor: palette.ink, backgroundColor: palette.groupFill },
  radio: { width: 18, height: 18, borderRadius: 9, borderWidth: 1, borderColor: palette.colors.text_color_tertiary, alignItems: 'center', justifyContent: 'center' },
  radioSelected: { borderColor: palette.ink, backgroundColor: palette.ink },
  radioDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: palette.inkInverse },
  packCopy: { flex: 1, minWidth: 0, gap: 3 },
  packTitle: { flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', gap: 5 },
  checkout: { gap: 9, marginTop: 2 },
  buy: { minHeight: 48, borderRadius: 13, backgroundColor: palette.ink, paddingHorizontal: 16, paddingVertical: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 },
  buyLabel: { color: palette.inkInverse, flexShrink: 1 },
  center: { textAlign: 'center' },
  bitcoin: { minHeight: 44, marginTop: 0 },
  trust: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' },
  status: { paddingVertical: 28, gap: 12, alignItems: 'center' },
  retry: { minHeight: 44, justifyContent: 'center', paddingHorizontal: 18, borderRadius: 10, backgroundColor: palette.groupFill },
});
